/**
 * Leave Policy Manager (LEAVE-REGISTRY-P3)
 * HR/Admin: entitlement rules by employment type, location, grade,
 * tenure (DOJ), probation status. Priority-ordered per leave type.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Scale, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY = {
  leave_type_id: '', name: '', entitled_days: 0, carry_forward_cap: 0,
  employment_type: '', work_location: '', grade_level: '',
  min_tenure_months: '', probation_allowed: true, prorate_first_year: true,
  priority: 10, is_active: true,
};

const LeavePolicyManager = () => {
  const [policies, setPolicies] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, tRes] = await Promise.all([
        axios.get(`${API}/api/leave-registry/policies`),
        axios.get(`${API}/api/leave-quota/types`),
      ]);
      setPolicies(pRes.data.policies || []);
      setTypes(tRes.data.leave_types || []);
    } catch {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...form,
        entitled_days: Number(form.entitled_days) || 0,
        carry_forward_cap: Number(form.carry_forward_cap) || 0,
        priority: Number(form.priority) || 10,
        employment_type: form.employment_type || null,
        work_location: form.work_location || null,
        grade_level: form.grade_level || null,
        min_tenure_months: form.min_tenure_months === '' ? null : Number(form.min_tenure_months),
      };
      await axios.post(`${API}/api/leave-registry/policies`, payload);
      toast.success('Policy created');
      setForm(EMPTY);
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/leave-registry/policies/${id}`);
      toast.success('Policy deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete policy');
    }
  };

  const typeName = (id) => types.find(t => t.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="leave-policy-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Leave Policies</h3>
        </div>
        <button onClick={() => setShowForm(s => !s)} data-testid="policy-add-toggle"
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
          <Plus className="w-4 h-4" /> New Policy
        </button>
      </div>
      <p className="text-sm text-gray-500 -mt-2">
        Entitlements resolve in order: employee override → matching policy (lowest priority number wins) → role quota → type default. Unset criteria match everyone; first-year entitlements prorate by joining month.
      </p>

      {showForm && (
        <form onSubmit={handleSave} className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Leave Type *</label>
            <select required value={form.leave_type_id} onChange={e => set('leave_type_id', e.target.value)}
              data-testid="policy-type-select" className="w-full px-2 py-2 border rounded-lg">
              <option value="">Select…</option>
              {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Policy Name *</label>
            <input required minLength={2} value={form.name} onChange={e => set('name', e.target.value)}
              data-testid="policy-name-input" className="w-full px-2 py-2 border rounded-lg" placeholder="e.g. Full-time onsite" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Entitled Days *</label>
            <input required type="number" step="0.5" min="0" value={form.entitled_days}
              onChange={e => set('entitled_days', e.target.value)} data-testid="policy-days-input"
              className="w-full px-2 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Priority (low = first)</label>
            <input type="number" min="1" value={form.priority} onChange={e => set('priority', e.target.value)}
              className="w-full px-2 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Employment Type</label>
            <input value={form.employment_type} onChange={e => set('employment_type', e.target.value)}
              className="w-full px-2 py-2 border rounded-lg" placeholder="any" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Work Location</label>
            <input value={form.work_location} onChange={e => set('work_location', e.target.value)}
              className="w-full px-2 py-2 border rounded-lg" placeholder="any" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Grade / Level</label>
            <input value={form.grade_level} onChange={e => set('grade_level', e.target.value)}
              className="w-full px-2 py-2 border rounded-lg" placeholder="any" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Min Tenure (months)</label>
            <input type="number" min="0" value={form.min_tenure_months}
              onChange={e => set('min_tenure_months', e.target.value)}
              className="w-full px-2 py-2 border rounded-lg" placeholder="none" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={form.probation_allowed} onChange={e => set('probation_allowed', e.target.checked)} />
            Allowed during probation
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={form.prorate_first_year} onChange={e => set('prorate_first_year', e.target.checked)} />
            Prorate first year (DOJ)
          </label>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Carry Forward Cap</label>
            <input type="number" step="0.5" min="0" value={form.carry_forward_cap}
              onChange={e => set('carry_forward_cap', e.target.value)} className="w-full px-2 py-2 border rounded-lg" />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={saving} data-testid="policy-save-btn"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Policy
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : policies.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8" data-testid="policies-empty">No policies configured — role quotas / type defaults apply.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="policies-list">
          {policies.map(p => (
            <div key={p.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <div>
                <span className="font-medium text-gray-800">{p.name}</span>
                <span className="text-gray-500 ml-2">{typeName(p.leave_type_id)} — {p.entitled_days} days/yr</span>
                <div className="text-xs text-gray-400 mt-0.5">
                  {[p.employment_type && `Type: ${p.employment_type}`,
                    p.work_location && `Location: ${p.work_location}`,
                    p.grade_level && `Grade: ${p.grade_level}`,
                    p.min_tenure_months != null && `Tenure ≥ ${p.min_tenure_months}mo`,
                    !p.probation_allowed && 'No probation',
                    `Priority ${p.priority}`].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button onClick={() => handleDelete(p.id)} data-testid={`policy-delete-${p.id}`}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeavePolicyManager;
